import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

import { AdminFeatureFlags } from '@reach/interfaces/user/admin-feature-flags.enum';
import { AdminToolsFeatureFlags } from '@reach/interfaces/user/user-feature-flags';
import { UserInPing } from '@reach/interfaces/user/user-in-ping';

import { apiUserParser } from '~/session/parsers';
import { ReachSession } from '~/session/types';

import { SessionApiService } from './session.api.service';

@Injectable({ providedIn: 'root' })
export class SessionService {
	private readonly sessionApi = inject(SessionApiService);
	private readonly router = inject(Router);

	private sessionChecked = false;

	private readonly user$$ = new BehaviorSubject<ReachSession | null>(null);
	public readonly user$ = this.user$$.asObservable();

	public async getAdminRole(): Promise<AdminFeatureFlags | null> {
		return await this.doAndLoadUserIfNecessary(async () => {
			return this.user$$.value?.adminRole ?? null;
		});
	}

	public hasPermission(permission: AdminToolsFeatureFlags): boolean {
		const user = this.user$$.getValue();
		const isAdmin = user?.adminRole === AdminFeatureFlags.SUPER_ADMIN;
		const hasFlag = user?.adminToolsFlags.includes(permission);

		return isAdmin || hasFlag || false;
	}

	public async hasPermissionAndLoadUser(permission: AdminToolsFeatureFlags): Promise<boolean> {
		return await this.doAndLoadUserIfNecessary(async () => {
			return this.hasPermission(permission);
		});
	}

	public async canAccessPage(): Promise<boolean> {
		const permissionsToCheck = Object.values(AdminToolsFeatureFlags);
		let hasPermission = false;
		for (const permission of permissionsToCheck) {
			hasPermission = await this.hasPermissionAndLoadUser(permission);
			if (hasPermission) {
				break;
			}
		}
		return hasPermission;
	}

	public async login(username: string, password: string): Promise<void> {
		const res = await this.sessionApi.login(username, password);
		this.setUser(res);
		this.sessionChecked = true;
		await this.router.navigate(['/']);
	}

	public async logout(): Promise<void> {
		try {
			await this.sessionApi.logout();
			this.sessionChecked = false;
		} catch (error) {
			console.error(error);
		}
		this.setUser(null);
		await this.router.navigate(['/']);
	}

	private setUser(user: UserInPing | null): void {
		this.user$$.next(apiUserParser(user));
	}

	private async doAndLoadUserIfNecessary<T>(fn: () => Promise<T>): Promise<T> {
		if (!this.sessionChecked) {
			const res = await this.sessionApi.ping().catch(() => null);
			this.setUser(res);
			this.sessionChecked = true;
		}

		return await fn();
	}
}
